import { useEffect, useState } from "react";
import { Col, FormGroup, Input, Label, Row } from "reactstrap";
import Select from "react-select";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import FixedSave from "../../../../components/Actions/FixedSave";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { get, post } from "../../../../utils/helpers/api";
import { toast } from "react-toastify";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import { useSettings } from "../../../../context/settings";

function CreateBillPage() {
  const { t } = useTranslation();
  const { datePickerFormat } = useSettings();
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [initLoading, setInitLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [suppliersData, setSuppliersData] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersData, setOrdersData] = useState([]);
  const [billData, setBillData] = useState({
    bill_number: state?.billData?.bill_number || "",
    supplier_id: state?.billData?.supplier_id || "",
    order_id: state?.billData?.order_id || "",
    due_date: state?.billData?.due_date ? parseISO(state.billData.due_date) : null,
    total_amount: state?.billData?.total_amount || "",
    notes: state?.billData?.notes || "",
  });

  useEffect(() => {
    initData();
  }, []);

  useEffect(() => {
    if (id && !state?.billData) {
      getBill();
    }
  }, [id]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (billData.supplier_id) {
        setOrdersLoading(true);
        const data = await get(`/purchases/bills/get-orders-by-supplier/${billData.supplier_id}`);
        if (data.success) {
          setOrdersData(data.orders);
        }
      } else {
        setOrdersData([]);
      }

      setOrdersLoading(false);
    };

    fetchOrders();
  }, [billData.supplier_id]);

  const initData = async () => {
    setInitLoading(true);
    const data = await get('/purchases/bills/create');
    if (data.success) {
      setSuppliersData(data.suppliers);
      if (!billData.bill_number) {
        setBillData(prev => ({ ...prev, bill_number: data.bill_number }));
      }
    }
    setInitLoading(false);
  };

  const getBill = async () => {
    const data = await get('/purchases/bills/view/' + id);
    if (data?.data) {
      setBillData(data.data);
    }
  };

  const saveBill = async () => {
    if (!billData.supplier_id) {
      toast.error(t('purchases.selectSupplierRequired'));
      return;
    }
    setSaveLoading(true);
    const url = id ? '/purchases/bills/update/' + id : '/purchases/bills/store';
    const data = await post(url, {
      ...billData,
      due_date: billData.due_date ? format(billData.due_date, 'yyyy-MM-dd') : null,
    });
    if (data.success) {
      navigate('/purchases/bills');
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  };

  const supplierOptions = suppliersData.map(s => ({
    value: s.id,
    label: s.company_name || [s.firstname, s.lastname].filter(Boolean).join(' ') || s.id
  }));

  const orderOptions = ordersData.map(o => ({
    value: o.id,
    label: o.order_number || o.id
  }));

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('purchases.bills'), id ? t('common.edit') : t('purchases.newBill')]}
          onBack={() => navigate('/purchases/bills')}
        />
        <div className="subheader-page-content scrollable">
          <Row>
            <Col md="6" className="mb-3">
              <Label>{t("purchases.supplier")}</Label>
              <Select
                options={supplierOptions}
                styles={SelectCustomStyles}
                isLoading={initLoading}
                value={supplierOptions.find(o => o.value === billData.supplier_id) || null}
                onChange={selected => setBillData({ ...billData, supplier_id: selected ? selected.value : '' })}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('purchases.purchaseOrder')}</Label>
              <Select
                options={orderOptions}
                styles={SelectCustomStyles}
                isLoading={ordersLoading}
                value={orderOptions.find(o => o.value === billData.order_id) || null}
                onChange={selected => setBillData({ ...billData, order_id: selected ? selected.value : '' })}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t("purchases.billNumber")}</Label>
              <Input
                value={billData.bill_number}
                readOnly
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t("sales.dueDate")}</Label>
              <DatePicker
                selected={billData.due_date}
                onChange={(d) => setBillData({ ...billData, due_date: d })}
                className="form-control"
                dateFormat={datePickerFormat}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t("common.totalAmount")} <span className="text-danger">*</span></Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={billData.total_amount}
                onChange={e => setBillData({ ...billData, total_amount: e.target.value })}
              />
            </Col>
          </Row>

          <FormGroup>
            <Label>{t("common.notes")}</Label>
            <Input
              type="textarea"
              rows="4"
              value={billData.notes}
              onChange={e => setBillData({ ...billData, notes: e.target.value })}
            />
          </FormGroup>
        </div>
      </div>

      <FixedSave
        onSave={saveBill}
        loading={saveLoading}
        disabled={saveLoading || initLoading || !billData.supplier_id || !billData.bill_number || !billData.total_amount}
      />
    </SidebarLayout>
  );
}

export default CreateBillPage;
